import { useEffect, useState } from 'react';
import { usePortalSession } from '../components/ClientPortal';
import { getPhotoVersion } from '../utils/photoCache';

// Reuse signed URLs briefly within the same portal session. Image bytes stay in
// the browser HTTP cache; private-library Cache Storage is never used here.
const urls = new Map();
const pending = new Map();
const TTL = 60_000; // The endpoint issues URLs valid for one hour.
const LIMIT = 256;

export default function usePortalImageUrl(image, variant) {
  const { sessionToken, headers } = usePortalSession();
  const key = JSON.stringify([sessionToken, image?.id, getPhotoVersion(image), variant]);
  const [state, setState] = useState(null);
  const cached = urls.get(key);
  const fresh = cached && cached.expires > Date.now() ? cached : null;

  useEffect(() => {
    if (!image?.id || !sessionToken) return;
    let cancelled = false;
    const cached = urls.get(key);
    if (cached?.expires > Date.now()) {
      setState({ key, url: cached.url });
      return;
    }
    let request = pending.get(key);
    if (!request) {
      request = fetch(`/api/portal/image/${image.id}/${variant}`, { headers })
        .then(async response => {
          if (!response.ok) return null;
          const { url } = await response.json();
          if (!url) return null;
          // Expired records and old sessions must not grow indefinitely.
          for (const [k, entry] of urls) if (entry.expires <= Date.now()) urls.delete(k);
          urls.delete(key);
          urls.set(key, { url, expires: Date.now() + TTL });
          while (urls.size > LIMIT) urls.delete(urls.keys().next().value);
          return url;
        }).catch(() => null).finally(() => pending.delete(key));
      pending.set(key, request);
    }
    request.then(url => { if (!cancelled) setState({ key, url }); });
    return () => { cancelled = true; };
  }, [key]);

  return {
    url: fresh?.url || (state?.key === key ? state.url : null),
    loading: !fresh && state?.key !== key,
    identity: key,
  };
}
